import { FANTASY_API_VERSION, URLS } from '@/data';
import { useUserStore } from '@/hooks/use-user-store';
import { Status } from '@/types/api.generated';
import { CashoutResponse, PerformCashoutRequest } from '@/types/cashout.generated';
import { logger } from '@/utils/logging';
import { useMutation, useQuery } from '@tanstack/react-query';

const LOG_TAG = '[use-cash-out]';

/** Minimal entry shape required by the cash-out polling and mutation hooks. */
export type CashableEntry = {
    id: string;
    status?: Status;
    canBeCashedout?: boolean;
};

export const cashOutPollingKey = (entryId: string) => ['cashout-amount-polling', entryId] as const;

export const useCashOutPolling = (item: CashableEntry, enabled: boolean) => {
    const { data } = useQuery<CashoutResponse>({
        queryKey: cashOutPollingKey(item.id),
        queryFn: () => fetchCashoutAmount(item.id),
        enabled: enabled,
        refetchInterval: 10000,
        staleTime: 10000,
    });

    return data?.cashoutAmount;
};

const fetchCashoutAmount = async (entryId: string) => {
    const url = new URL(`${URLS.FANTASY_REST_API_URL}/cashout/${entryId}`);

    const response = await fetch(url, {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json',
            Authorization: useUserStore.getState().session?.access_token ?? '',
            'Fantasy-Api-Version': FANTASY_API_VERSION,
        },
    });

    let data;
    try {
        data = await response.json();
    } catch {
        logger.warn(LOG_TAG, 'Failed to parse cashout response body');
        return null;
    }

    if (!response.ok) {
        throw data;
    }

    return data;
};

const cashoutEntry = async (entryId: string, cashoutAmount: number): Promise<CashoutResponse> => {
    const url = new URL(`${URLS.FANTASY_REST_API_URL}/cashout`);
    const body: PerformCashoutRequest = { entryId, cashoutAmount };

    const response = await fetch(url, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            Authorization: useUserStore.getState().session?.access_token ?? '',
            'Fantasy-Api-Version': FANTASY_API_VERSION,
        },
        body: JSON.stringify(body),
    });
    const data = await response.json();

    if (!response.ok) {
        throw data;
    }
    return data;
};

export const usePerformCashoutEntry = () => {
    return useMutation({
        mutationKey: ['cashout'],
        mutationFn: ({ entryId, amount }: { entryId: string; amount: number }) => cashoutEntry(entryId, amount),
    });
};
